import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Play, BarChart3, RefreshCw, Square, CheckCircle2 } from 'lucide-react';
interface SlideWhyItWorksProps {
  onContinue: () => void;
}
export function SlideWhyItWorks({
  onContinue
}: SlideWhyItWorksProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 800);
    const t2 = setTimeout(() => setStage(2), 1800);
    const t3 = setTimeout(() => setStage(3), 2800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-14 md:py-12 lg:px-20 lg:py-14 max-w-7xl mx-auto" data-id="element-493">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-3xl md:text-4xl lg:text-5xl font-serif leading-tight text-white mb-10 text-center" data-id="element-494">
        Why this <span className="text-teal-400" data-id="element-495">works</span>
      </motion.h2>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-id="element-496">
        {/* Main Visual — Split */}
        <div className="lg:col-span-9 grid grid-cols-2 gap-6 items-center" data-id="element-497">
          {/* LEFT — Today (greyed out) */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          x: stage >= 1 ? 0 : -20
        }} className="relative" data-id="element-498">
            <div className="bg-navy-800/80 border border-navy-700 rounded-2xl p-8 opacity-60" data-id="element-499">
              <span className="text-xs uppercase tracking-widest text-white/30 font-semibold mb-6 block" data-id="element-500">
                Today
              </span>

              {/* Linear dead-end flow */}
              <div className="flex flex-col items-center gap-4" data-id="element-501">
                {['Build', 'Launch', 'Stop'].map((step, idx) => <div key={idx} className="flex items-center gap-3 w-full" data-id="element-502">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${idx === 2 ? 'bg-red-500/20 border border-red-500/30' : 'bg-navy-700 border border-navy-600'}`} data-id="element-503">
                      {idx === 2 ? <Square className="w-4 h-4 text-red-400/60" data-id="element-504" /> : idx === 0 ? <div className="w-3 h-3 rounded bg-white/20" data-id="element-505" /> : <Play className="w-4 h-4 text-white/30" data-id="element-506" />}
                    </div>
                    <span className={`text-sm font-medium ${idx === 2 ? 'text-red-400/50' : 'text-white/30'}`} data-id="element-507">
                      {step}
                    </span>
                    {idx < 2 && <div className="flex-1 h-[1px] bg-white/10 ml-2" data-id="element-508" />}
                  </div>)}
              </div>

              {/* Dead end indicator */}
              <div className="mt-4 text-center" data-id="element-509">
                <div className="inline-block bg-red-500/10 border border-red-500/20 rounded-full px-3 py-1" data-id="element-510">
                  <span className="text-xs text-red-400/50" data-id="element-511">Dead end</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT — Snakker (highlighted) */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          x: stage >= 2 ? 0 : 20
        }} className="relative" data-id="element-512">
            <div className="bg-teal-400/5 border border-teal-400/30 rounded-2xl p-8 shadow-[0_0_30px_rgba(0,212,170,0.05)]" data-id="element-513">
              <span className="text-xs uppercase tracking-widest text-teal-400 font-semibold mb-6 block" data-id="element-514">
                Snakker
              </span>

              {/* Loop flow */}
              <div className="relative" data-id="element-515">
                <div className="flex flex-col items-center gap-4" data-id="element-516">
                  {[{
                  icon: <Play className="w-4 h-4" data-id="element-517" />,
                  label: 'Campaign'
                }, {
                  icon: <BarChart3 className="w-4 h-4" data-id="element-518" />,
                  label: 'Data'
                }, {
                  icon: <RefreshCw className="w-4 h-4" data-id="element-519" />,
                  label: 'Improve'
                }].map((step, idx) => <motion.div key={idx} initial={{
                  opacity: 0
                }} animate={{
                  opacity: stage >= 2 ? 1 : 0
                }} transition={{
                  delay: idx * 0.2
                }} className="flex items-center gap-3 w-full" data-id="element-520">
                      <div className="w-10 h-10 rounded-lg bg-teal-400/15 border border-teal-400/30 flex items-center justify-center text-teal-400" data-id="element-521">
                        {step.icon}
                      </div>
                      <span className="text-sm font-medium text-white/80" data-id="element-522">
                        {step.label}
                      </span>
                      {idx < 2 && <div className="flex-1 h-[1px] bg-teal-400/20 ml-2" data-id="element-523" />}
                    </motion.div>)}
                </div>

                {/* Loop-back arrow */}
                <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: stage >= 2 ? 1 : 0
              }} transition={{
                delay: 0.8
              }} className="absolute -right-2 top-0 bottom-0 w-6 flex items-center" data-id="element-524">
                  <div className="h-full w-[2px] bg-teal-400/20 rounded-full relative" data-id="element-525">
                    <motion.div className="absolute -top-1 -left-[5px] w-3 h-3 border-t-2 border-l-2 border-teal-400/40 rounded-tl" animate={{
                    opacity: [0.3, 1, 0.3]
                  }} transition={{
                    repeat: Infinity,
                    duration: 2
                  }} data-id="element-526" />
                  </div>
                </motion.div>
              </div>

              {/* Always on badge */}
              <div className="mt-4 text-center" data-id="element-527">
                <div className="inline-block bg-teal-400/10 border border-teal-400/20 rounded-full px-3 py-1" data-id="element-528">
                  <span className="text-xs text-teal-400 font-medium" data-id="element-529">
                    Always on · Always improving
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Center overlay text + Insight */}
        <div className="lg:col-span-3" data-id="element-530">
          {/* Big center concept */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          scale: stage >= 3 ? 1 : 0.9
        }} transition={{
          duration: 0.5
        }} className="space-y-6" data-id="element-531">
            <div className="bg-navy-700/50 border border-navy-600 rounded-2xl p-5" data-id="element-532">
              <div className="text-teal-400 font-medium mb-3 flex items-center gap-2 text-sm" data-id="element-533">
                <CheckCircle2 className="w-4 h-4" data-id="element-534" />
                Insight
              </div>
              <div className="space-y-2" data-id="element-535">
                <div className="flex items-center gap-2" data-id="element-536">
                  <span className="text-xs text-white/40 uppercase tracking-wider" data-id="element-537">
                    Before:
                  </span>
                  <span className="text-sm text-white/50" data-id="element-538">One-off</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-539">
                  <span className="text-xs text-teal-400/70 uppercase tracking-wider" data-id="element-540">
                    Now:
                  </span>
                  <span className="text-sm text-teal-300 font-medium" data-id="element-541">
                    Continuous
                  </span>
                </div>
              </div>
            </div>

            <Button onClick={onContinue} className="w-full" data-id="element-542">
              Apply to Columvi
            </Button>
          </motion.div>
        </div>
      </div>
    </div>;
}